import {
  CandidateList,
  CandidateListFilters,
  type CandidateFiltersQuery,
} from '@features/candidate';
import {
  useGetVacancy,
  useGetVacancyCandidates,
  type GetVacancyCandidatesRequest,
} from '@entities/vacancy';
import { PageContainer } from '@shared/ui';
import { PaginateType, type SortQuery } from '@shared/types';
import { useBreadcrumbs } from '@shared/lib';
import { Anchor, Flex, Text } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import { NavLink, useParams, useSearchParams } from 'react-router';

export const VacancySelectedCandidatesPage = () => {
  const { id } = useParams();
  const vacancyId = Number(id);
  const [filter, setFilter] = useState<CandidateFiltersQuery>({});
  const [sort, setSort] = useState<SortQuery>({ id: 'desc' });

  const [searchParams, setSearchParams] = useSearchParams({
    page: '1',
    perPage: '15',
  });

  const [page, setPage] = useState(Number(searchParams.get('page')));
  const [pageSize, setPageSize] = useState(Number(searchParams.get('perPage')));

  const { data: vacancy } = useGetVacancy({
    id: vacancyId,
  });

  const mergedFilter = useMemo(
    () => ({
      ...filter,
      decisionType: 'POSITIVE' as const,
    }),
    [filter]
  );

  const {
    data: candidates,
    isLoading,
    isFetching,
  } = useGetVacancyCandidates(vacancyId, {
    filter: mergedFilter as NonNullable<GetVacancyCandidatesRequest['filter']>,
    sort,
    perPage: pageSize,
    page,
    paginate: PaginateType.DEFAULT,
    enabled: Boolean(vacancyId),
  });

  const context = useBreadcrumbs();

  useEffect(() => {
    if (!context || !vacancy?.name || !vacancyId) {
      return;
    }

    context.setBreadcrumb(
      <Flex gap={4} wrap='nowrap' align='center'>
        <Anchor to='/vacancy-list' component={NavLink} variant='gray'>
          Вакансии
        </Anchor>
        <Text>/</Text>
        <Anchor
          to={`/vacancy-list/${vacancyId}`}
          component={NavLink}
          variant='gray'
        >
          {vacancy.name}
        </Anchor>
        <Text>/</Text>
        <Text c='tertiary.4' truncate='end' maw='40dvw'>
          Выбранные кандидаты
        </Text>
      </Flex>
    );

    return () => {
      context.clearBreadcrumb();
    };
  }, [vacancy?.name, vacancyId]);

  const handleSortChange = (field: string, direction?: 'asc' | 'desc') => {
    if (!direction) {
      setSort({ id: 'desc' });
      return;
    }

    setSort({
      [field]: direction,
    });
  };

  return (
    <PageContainer title='Выбранные кандидаты'>
      <CandidateListFilters
        setFilter={data => {
          setFilter(data);
          setPage(1);
        }}
      />

      <CandidateList
        candidates={candidates?.data || []}
        page={page}
        setPage={(next: number) => {
          setPage(next);
          setSearchParams({
            page: next.toString(),
            perPage: pageSize.toString(),
          });
        }}
        pageSize={pageSize}
        setPageSize={(size: number) => {
          setPageSize(size);
          setSearchParams({ page: '1', perPage: size.toString() });
        }}
        isFilledFilters={Boolean(Object.keys(filter).length)}
        onSortChange={handleSortChange}
        currentSort={sort}
        isLoading={isLoading || isFetching}
        totalRecords={candidates?.pagination?.total || 0}
      />
    </PageContainer>
  );
};
